import { Routes } from '@angular/router';
import { registerFlowGuard } from '../core/auth/guards/register-flow.guard';
import { registerRedirectGuard } from '../core/auth/guards/register-redirect.guard';
import { roleGuard } from '../core/auth/guards/role.guard';
import { authGuard } from '../core/auth/guards/auth.guard';
import { guestGuard } from '../core/auth/guards/guest.guard';
import { subscriptionGuard } from '../core/auth/guards/subscription.guard';

export const routes: Routes = [
    /**
    * =========================
    * PUBLIC ROUTES
    * =========================
    */

    {
        path: '',

        loadComponent: () =>
            import('../layouts/public-layout/public-layout.component')
                .then((m) => m.PublicLayoutComponent),

        children: [

            {
                path: '',
                pathMatch: 'full',
                loadComponent: () =>
                    import('../features/public/home/home.component')
                        .then((m) => m.HomeComponent),
                data: {
                    seo: {
                        title: 'WEKO | Trade like a PRO with All-in-One Platform',
                        description: 'Trade with Multiple Crypto, Forex and Commodity. WEKO is a place where Traders and Investors meet.',
                        keywords: 'weko, trading platform, forex trading, crypto trading, trader signals'
                    }
                }
            },
            {
                path: 'auth',
                canActivate: [guestGuard],

                children: [

                    {
                        path: 'login',
                        loadComponent: () =>
                            import('../features/auth/login/login.component')
                                .then((m) => m.LoginComponent),
                        data: {
                            seo: {
                                title: 'Login | WEKO Pro Platform',
                                description: 'Login to your WEKO Pro trading account.',
                                robots: 'noindex, follow'
                            }
                        }
                    },
                    {
                        path: 'register',

                        children: [

                            {
                                path: '',
                                pathMatch: 'full',
                                canActivate: [registerRedirectGuard],
                                children: []
                            },
                            {
                                path: 'choice',
                                loadComponent: () =>
                                    import(
                                        '../features/auth/register/register-choice/register-choice.component'
                                    ).then(
                                        (m) => m.RegisterChoiceComponent
                                    ),
                            },
                            {
                                path: 'details',
                                canActivate: [registerFlowGuard],

                                loadComponent: () =>
                                    import(
                                        '../features/auth/register/register/register.component'
                                    ).then(
                                        (m) => m.RegisterComponent
                                    ),
                                data: { seo: { title: 'Register Details | WEKO Pro', robots: 'noindex, follow' } }
                            },
                            {
                                path: 'email-otp',
                                canActivate: [registerFlowGuard],

                                loadComponent: () =>
                                    import(
                                        '../features/auth/register/email-otp/email-otp.component'
                                    ).then(
                                        (m) => m.EmailOtpComponent
                                    ),
                                data: { seo: { title: 'Verify Email | WEKO Pro', robots: 'noindex, follow' } }
                            },
                            {
                                path: 'phone',
                                canActivate: [registerFlowGuard],

                                loadComponent: () =>
                                    import(
                                        '../features/auth/register/phone-verification/phone-verification.component'
                                    ).then(
                                        (m) => m.PhoneVerificationComponent
                                    ),
                                data: { seo: { title: 'Phone Verification | WEKO Pro', robots: 'noindex, follow' } }
                            },
                            {
                                path: 'phone-otp',
                                canActivate: [registerFlowGuard],

                                loadComponent: () =>
                                    import(
                                        '../features/auth/register/phone-otp/phone-otp.component'
                                    ).then(
                                        (m) => m.PhoneOtpComponent
                                    ),
                                data: { seo: { title: 'Verify Phone | WEKO Pro', robots: 'noindex, follow' } }
                            },
                            {
                                path: 'complete-profile',
                                canActivate: [registerFlowGuard],

                                loadComponent: () =>
                                    import(
                                        '../features/auth/register/complete-profile/complete-profile.component'
                                    ).then(
                                        (m) => m.CompleteProfileComponent
                                    ),
                                data: { seo: { title: 'Complete Profile | WEKO Pro', robots: 'noindex, follow' } }
                            },
                            {
                                path: 'trader-evaluation',
                                loadComponent: () =>
                                    import('../features/auth/register/trader-evaluation/trader-evaluation.component')
                                        .then(c => c.TraderEvaluationComponent),
                                data: { seo: { title: 'Trader Evaluation | WEKO Pro', robots: 'noindex, follow' } }
                            }

                        ]
                    },
                    {
                        path: 'forgot-password',

                        loadComponent: () =>
                            import(
                                '../features/auth/forgot-password/forgot-password.component'
                            ).then(
                                (m) => m.ForgetPasswordComponent
                            ),
                        data: { seo: { title: 'Forgot Password | WEKO Pro', robots: 'noindex, follow' } }
                    },
                    {
                        path: 'reset-password',

                        loadComponent: () =>
                            import(
                                '../features/auth/reset-password/reset-password.component'
                            ).then(
                                (m) => m.ResetPasswordComponent
                            ),
                        data: { seo: { title: 'Reset Password | WEKO Pro', robots: 'noindex, follow' } }
                    },
                    {
                        path: 'reset-password/:token',

                        loadComponent: () =>
                            import(
                                '../features/auth/reset-password/reset-password.component'
                            ).then(
                                (m) => m.ResetPasswordComponent
                            ),
                        data: { seo: { title: 'Reset Password | WEKO Pro', robots: 'noindex, follow' } }
                    }

                ]
            },
            {
                path: 'plans',
                loadComponent: () =>
                    import('../shared/ui/plans/plans.component')
                        .then((m) => m.PlansComponent),
                data: {
                    seo: {
                        title: 'Subscription Plans & Pricing | WEKO Pro',
                        description: 'Explore WEKO Pro membership plans to access top trader strategies, AI tools, and analytics.',
                        keywords: 'weko plans, weko subscription, trading pricing'
                    }
                }
            },
            {
                path: 'privacy-policy',
                loadComponent: () =>
                    import('../shared/ui/footer/privacy-policy/privacy-policy.component').then(
                        (m) => m.PrivacyPolicyComponent
                    ),
                data: {
                    seo: {
                        title: 'Privacy Policy | WEKO Pro',
                        description: 'Official privacy policy of WEKO Global Markets Ltd. Learn how we handle and protect user data.',
                        keywords: 'weko privacy policy, privacy notice'
                    }
                }
            },
            {
                path: 'refund-policy',
                loadComponent: () =>
                    import('../shared/ui/footer/refund-policy/refund-policy.component').then(
                        (m) => m.RefundPolicyComponent
                    ),
                data: {
                    seo: {
                        title: 'Refund Policy | WEKO Pro',
                        description: 'Official refund policy and eligibility guidelines for WEKO Pro subscriptions.',
                        keywords: 'weko refund policy, subscription refund'
                    }
                }
            },
            {
                path: 'disclosure',
                loadComponent: () =>
                    import('../shared/ui/footer/disclosure/disclosure.component').then(
                        (m) => m.DisclosureComponent
                    ),
                data: {
                    seo: {
                        title: 'Risk Disclosure | WEKO Pro',
                        description: 'Important risk disclosures and disclaimers regarding simulated and live trading.',
                        keywords: 'weko risk disclosure, trading disclaimer'
                    }
                }
            },
            {
                path: 'grievance-policy',
                loadComponent: () =>
                    import('../shared/ui/footer/grievance-policy/grievance-policy.component').then(
                        (m) => m.GrievancePolicyComponent
                    ),
                data: {
                    seo: {
                        title: 'Grievance Redressal Policy | WEKO Pro',
                        description: 'Grievance officer contact and dispute resolution process for WEKO Global Markets.',
                        keywords: 'weko grievance policy, customer grievance'
                    }
                }
            },
            {
                path: 'terms-and-conditions',
                loadComponent: () =>
                    import('../shared/ui/footer/terms-and-conditions/terms-and-conditions.component').then(
                        (m) => m.TermsAndConditionsComponent
                    ),
                data: {
                    seo: {
                        title: 'Terms & Conditions | WEKO Pro',
                        description: 'Terms of service and user agreements governing the use of WEKO Pro platform.',
                        keywords: 'weko terms, terms of service'
                    }
                }
            },
            {
                path: 'broker',
                loadComponent: () =>
                    import('../shared/ui/broker/broker.component').then(
                        (m) => m.BrokerComponent
                    ),
                data: {
                    seo: {
                        title: 'Supported Brokers | WEKO Pro',
                        description: 'Discover top forex and crypto brokers supported on the WEKO trading platform.',
                        keywords: 'weko brokers, forex brokers, crypto brokers'
                    }
                }
            },
            {
                path: 'about',
                loadComponent: () =>
                    import('../shared/ui/about/about.component').then(
                        (m) => m.AboutComponent
                    ),
                data: {
                    seo: {
                        title: 'About Us | WEKO Pro Trading Platform',
                        description: 'Learn about WEKO Global Markets Ltd., our vision, and how we empower traders and investors.',
                        keywords: 'about weko, weko global markets, trading ecosystem'
                    }
                }
            },
            {
                path: 'careers',
                loadComponent: () =>
                    import('../shared/ui/careers/careers.component').then(
                        (m) => m.CareersComponent
                    ),
                data: {
                    seo: {
                        title: 'Careers at WEKO | Join Our Remote Team',
                        description: 'Join our mission to empower global traders. Build cutting-edge market research, algorithmic models, and financial technology with a world-class remote team.',
                        keywords: 'weko careers, jobs at weko, technical research analyst, trading jobs'
                    }
                }
            },
            {
                path: 'trader-program',
                loadComponent: () =>
                    import('../shared/ui/trader-program/trader-program.component').then(
                        (m) => m.TraderProgramComponent
                    ),
                data: {
                    seo: {
                        title: 'WEKO Trader Program — Practice, Build a Track Record, Get Recognized',
                        description: 'Share trading ideas in a simulated trading environment. Track performance, publish strategies risk-free, and build a following.',
                        keywords: 'weko trader program, simulated trading, lead trader, copy trading'
                    }
                }
            },

            {
                path: 'blog',
                loadComponent: () =>
                    import('../shared/ui/footer/blog/blog.component').then(
                        (m) => m.BlogComponent
                    ),
                data: {
                    seo: {
                        title: 'Trading Insights & Blog | WEKO Pro',
                        description: 'Read market analysis, trading guides, and platform updates on the WEKO blog.',
                        keywords: 'weko blog, trading analysis, forex blog, crypto news'
                    }
                }
            },

            {
                path: 'faq',
                loadComponent: () =>
                    import('../shared/ui/footer/faq/faq.component').then(
                        (m) => m.FaqComponent
                    ),
                data: {
                    seo: {
                        title: 'Frequently Asked Questions | WEKO Pro',
                        description: 'Find answers to common questions about WEKO Pro trading tools, accounts, and plans.',
                        keywords: 'weko faq, trading platform questions'
                    }
                }
            },

            {
                path: 'help-center',
                loadComponent: () =>
                    import('../shared/ui/footer/help-center/help-center.component').then(
                        (m) => m.HelpCenterComponent
                    ),
                data: {
                    seo: {
                        title: 'Help Center & Support | WEKO Pro',
                        description: 'Get support for your WEKO Pro account, platform navigation, and services.',
                        keywords: 'weko help center, weko support'
                    }
                }
            },
            {
                path: 'analytics',
                children: [
                    {
                        path: 'chart-analysis',
                        loadComponent: () =>
                            import('../shared/trading/analytics/chart-analysis/chart-analysis.component')
                                .then(m => m.ChartAnalysisComponent),
                        data: { seo: { title: 'Chart Analysis | WEKO Pro Analytics', description: 'Advanced market chart analysis tool.' } }
                    },
                    {
                        path: 'crypto-live',
                        loadComponent: () =>
                            import('../shared/trading/analytics/crypto-live/crypto-live.component')
                                .then(m => m.CryptoLiveComponent),
                        data: { seo: { title: 'Crypto Live Prices | WEKO Pro Analytics', description: 'Real-time cryptocurrency price tracking.' } }
                    },
                    {
                        path: 'crypto-trends',
                        loadComponent: () =>
                            import('../shared/trading/analytics/crypto-trends/crypto-trends.component')
                                .then(m => m.CryptoTrendsComponent),
                        data: { seo: { title: 'Crypto Market Trends | WEKO Pro Analytics', description: 'Identify trending cryptocurrencies and momentum.' } }
                    },
                    {
                        path: 'market-heatmap',
                        loadComponent: () =>
                            import('../shared/trading/analytics/market-heatmap/market-heatmap.component')
                                .then(m => m.MarketHeatmapComponent),
                        data: { seo: { title: 'Market Heatmap | WEKO Pro Analytics', description: 'Visual heatmap of global market movements.' } }
                    },
                    {
                        path: 'seasonality-analysis',
                        loadComponent: () =>
                            import('../shared/trading/analytics/seasonality-analysis/seasonality-analysis.component')
                                .then(m => m.SeasonalityAnalysisComponent),
                        data: { seo: { title: 'Seasonality Analysis | WEKO Pro Analytics', description: 'Historical seasonality patterns in financial markets.', robots: 'noindex, follow' } }
                    }

                ]
            },
            {
                path: 'tools',
                children: [
                    {
                        path: 'pip-calculator',
                        loadComponent: () =>
                            import('../shared/trading/calculators/pip-calculator/pip-calculator.component')
                                .then(m => m.PipCalculatorComponent),
                        data: { seo: { title: 'Pip Calculator | WEKO Pro Trading Tools', description: 'Calculate pip values for Forex and Crypto currency pairs.' } }
                    },
                    {
                        path: 'compound-interest',
                        loadComponent: () =>
                            import('../shared/trading/calculators/compound-interest/compound-interest.component')
                                .then(m => m.CompoundInterestComponent),
                        data: { seo: { title: 'Compound Interest Calculator | WEKO Pro', description: 'Simulate trading portfolio growth over time with compound interest.' } }
                    },

                ]
            },
            {
                path: 'ai',
                children: [
                    {
                        path: 'chart-analyzer',
                        loadComponent: () =>
                            import('../shared/trading/ai-suite/chart-analyzer/chart-analyzer.component')
                                .then(m => m.ChartAnalyzerComponent),
                        data: { seo: { title: 'AI Chart Analyzer | WEKO Pro AI Suite', description: 'AI-powered technical chart pattern recognition.', robots: 'noindex, follow' } }
                    },
                    {
                        path: 'strategy-generator',
                        loadComponent: () =>
                            import('../shared/trading/ai-suite/strategy-generator/strategy-generator.component')
                                .then(m => m.StrategyGeneratorComponent),
                        data: { seo: { title: 'AI Strategy Generator | WEKO Pro AI Suite', description: 'Generate customized trading strategies with AI.', robots: 'noindex, follow' } }
                    },
                    {
                        path: 'rr-assistant',
                        loadComponent: () =>
                            import('../shared/trading/ai-suite/rr-assistant/rr-assistant.component')
                                .then(m => m.RrAssistantComponent),
                        data: { seo: { title: 'Risk/Reward Assistant | WEKO Pro AI Suite', description: 'AI risk to reward ratio calculation assistant.', robots: 'noindex, follow' } }
                    },
                    {
                        path: 'rr-calculation',
                        loadComponent: () =>
                            import('../shared/trading/ai-suite/rr-calculation/rr-calculation.component')
                                .then(m => m.RrCalculationComponent),
                        data: { seo: { title: 'R/R Calculator | WEKO Pro AI Suite', description: 'Precise risk and reward ratio calculator.', robots: 'noindex, follow' } }
                    },
                    {
                        path: 'brain',
                        loadComponent: () =>
                            import('../shared/trading/ai-suite/ai-brain/ai-brain.component')
                                .then(m => m.AiBrainComponent),
                        data: { seo: { title: 'AI Brain | WEKO Pro AI Suite', description: 'Centralized AI trading intelligence engine.', robots: 'noindex, follow' } }
                    }
                ]
            },
            {
                path: 'contact-us',
                loadComponent: () =>
                    import('../shared/ui/footer/contact-us/contact-us.component').then(
                        (m) => m.ContactUsComponent
                    ),
                data: {
                    seo: {
                        title: 'Contact Us | WEKO Pro Support',
                        description: 'Get in touch with WEKO customer support team.',
                        keywords: 'contact weko, weko support'
                    }
                }
            },
            {
                path: 'sitemap',
                loadComponent: () =>
                    import('../shared/ui/footer/sitemap/sitemap.component').then(
                        (m) => m.SitemapComponent
                    ),
                data: {
                    seo: {
                        title: 'Sitemap | WEKO Pro Site Navigation',
                        description: 'Complete page list and site map for WEKO Pro platform.',
                        keywords: 'weko sitemap, site navigation'
                    }
                }
            },

            {
                path: 'weko-guide',
                loadComponent: () =>
                    import('../shared/ui/footer/weko-guide/weko-guide.component').then(
                        (m) => m.WekoGuideComponent
                    ),
                data: {
                    seo: {
                        title: 'WEKO Guide | Complete Trading Platform Guide',
                        description: 'Learn how to use WEKO Pro to study, compare, and trade Forex, Crypto & Commodities.',
                        keywords: 'weko guide, trading platform guide'
                    }
                }
            },

            //--------------Blog Route Start---------------
            {
                path: 'blog/:slug',
                loadComponent: () =>
                    import('../shared/ui/footer/blog-details/blog-details.component')
                        .then(m => m.BlogDetailsComponent),
                data: {
                    seo: {
                        title: 'Trading Article | WEKO Pro Blog',
                        description: 'Read detailed trading analysis and market insight articles on WEKO.'
                    }
                }
            },
            //--------------Blog Route End---------------

            //--------------SEO Legacy & Alias Redirects Start---------------
            { path: 'contact', redirectTo: 'contact-us', pathMatch: 'full' },
            { path: 'home', redirectTo: '', pathMatch: 'full' },
            { path: 'privacyPolicy', redirectTo: 'privacy-policy', pathMatch: 'full' },
            { path: 'memberTerms', redirectTo: 'terms-and-conditions', pathMatch: 'full' },
            { path: 'termsOfUse', redirectTo: 'terms-and-conditions', pathMatch: 'full' },
            { path: 'refundPolicy', redirectTo: 'refund-policy', pathMatch: 'full' },
            { path: 'career', redirectTo: 'careers', pathMatch: 'full' },
            { path: 'compliances', redirectTo: 'disclosure', pathMatch: 'full' },
            { path: 'features', redirectTo: '', pathMatch: 'full' },
            { path: 'blogs', redirectTo: 'blog', pathMatch: 'full' },
            //--------------SEO Legacy & Alias Redirects End---------------


        ]
    },

    /**
     * =========================
     * CLIENT DASHBOARD
     * =========================
     */
    {
        path: 'client',
        canActivate: [authGuard, roleGuard(['CLIENT'])],
        loadComponent: () =>
            import('../layouts/dashboard-layout/dashboard-layout.component').then(
                (m) => m.DashboardLayoutComponent
            ),
        children: [
            {
                path: '',
                redirectTo: 'dashboard',
                pathMatch: 'full',
            },
            {
                path: 'dashboard',
                loadComponent: () =>
                    import('../features/client/components/dashboard/dashboard.component').then(
                        (m) => m.DashboardComponent
                    ),
            },
            {
                path: 'portfolio',
                loadComponent: () =>
                    import('../shared/trading/portfolio/portfolio.component')
                        .then(m => m.PortfolioComponent),
                data: { seo: { title: 'Trading Portfolio | WEKO Pro', description: 'Manage Weko-Trade accounts, open positions, and trading history.' } }
            },
            {
                path: 'trades',
                loadComponent: () =>
                    import('../features/client/components/trades/trades.component')
                        .then(m => m.TradesComponent)
            },
            {
                path: 'my-following',
                redirectTo: 'my-trades',
                pathMatch: 'full'
            },

            {
                path: 'my-trades',
                loadComponent: () =>
                    import('../features/client/components/my-trades/my-trades.component')
                        .then(m => m.MyTradesComponent)
            },

            {
                path: 'trade-details/:id',
                loadComponent: () =>
                    import('..//features/client/components/trade-details/trade-details.component')
                        .then(m => m.TradeDetailsComponent)
            },
            {
                path: 'profile',
                loadComponent: () =>
                    import('../shared/account/profile/profile.component')
                        .then(m => m.ProfileComponent)
            },
            {
                path: 'change-password',
                loadComponent: () =>
                    import('../shared/account/change-password/change-password.component')
                        .then(m => m.ChangePasswordComponent)
            },
            {
                path: 'settings/change-password',
                loadComponent: () =>
                    import('../shared/account/change-password/change-password.component')
                        .then(m => m.ChangePasswordComponent)
            },
            {
                path: 'membership',
                loadComponent: () =>
                    import('../features/client/components/membership/membership.component')
                        .then(m => m.MembershipComponent)
            },
            {
                path: 'mtr-dashboard',
                loadComponent: () =>
                    import('../features/client/components/mtr-dashboard/mtr-dashboard.component')
                        .then(m => m.MtrDashboardComponent)
            },
            {
                path: 'leaderboard',
                loadComponent: () =>
                    import('../shared/trading/leaderboard/leaderboard.component')
                        .then(m => m.LeaderboardComponent)
            },
            {
                path: 'results',
                loadComponent: () =>
                    import('../shared/trading/results/results.component')
                        .then(m => m.ResultsComponent)
            },
            {
                path: 'tools',
                children: [
                    {
                        path: 'pip-calculator',
                        loadComponent: () =>
                            import('../shared/trading/calculators/pip-calculator/pip-calculator.component')
                                .then(m => m.PipCalculatorComponent)
                    },
                    {
                        path: 'compound-interest',
                        loadComponent: () =>
                            import('../shared/trading/calculators/compound-interest/compound-interest.component')
                                .then(m => m.CompoundInterestComponent)
                    },

                ]
            },
            {
                path: 'analytics',
                children: [
                    {
                        path: 'chart-analysis',
                        loadComponent: () =>
                            import('../shared/trading/analytics/chart-analysis/chart-analysis.component')
                                .then(m => m.ChartAnalysisComponent)
                    },
                    {
                        path: 'crypto-live',
                        loadComponent: () =>
                            import('../shared/trading/analytics/crypto-live/crypto-live.component')
                                .then(m => m.CryptoLiveComponent)
                    },
                    {
                        path: 'crypto-trends',
                        loadComponent: () =>
                            import('../shared/trading/analytics/crypto-trends/crypto-trends.component')
                                .then(m => m.CryptoTrendsComponent)
                    },
                    {
                        path: 'market-heatmap',
                        loadComponent: () =>
                            import('../shared/trading/analytics/market-heatmap/market-heatmap.component')
                                .then(m => m.MarketHeatmapComponent)
                    },
                    {
                        path: 'seasonality-analysis',
                        loadComponent: () =>
                            import('../shared/trading/analytics/seasonality-analysis/seasonality-analysis.component')
                                .then(m => m.SeasonalityAnalysisComponent)
                    }

                ]
            },
            {
                path: 'ai',
                children: [
                    {
                        path: 'chart-analyzer',
                        loadComponent: () =>
                            import('../shared/trading/ai-suite/chart-analyzer/chart-analyzer.component')
                                .then(m => m.ChartAnalyzerComponent)
                    },
                    {
                        path: 'strategy-generator',
                        loadComponent: () =>
                            import('../shared/trading/ai-suite/strategy-generator/strategy-generator.component')
                                .then(m => m.StrategyGeneratorComponent)
                    },
                    {
                        path: 'rr-assistant',
                        loadComponent: () =>
                            import('../shared/trading/ai-suite/rr-assistant/rr-assistant.component')
                                .then(m => m.RrAssistantComponent)
                    },
                    {
                        path: 'rr-calculation',
                        loadComponent: () =>
                            import('../shared/trading/ai-suite/rr-calculation/rr-calculation.component')
                                .then(m => m.RrCalculationComponent)
                    },
                    {
                        path: 'brain',
                        loadComponent: () =>
                            import('../shared/trading/ai-suite/ai-brain/ai-brain.component')
                                .then(m => m.AiBrainComponent)
                    }
                ]
            }
        ],
    },

    /**
     * =========================
     * TRADER DASHBOARD
     * =========================
     */
    {
        path: 'trader',
        canActivate: [authGuard, roleGuard(['TRADER'])],
        loadComponent: () =>
            import('../layouts/dashboard-layout/dashboard-layout.component').then(
                (m) => m.DashboardLayoutComponent
            ),
        children: [
            {
                path: '',
                redirectTo: 'dashboard',
                pathMatch: 'full',
            },
            {
                path: 'dashboard',
                loadComponent: () =>
                    import('../features/trader/components/dashboard/dashboard.component').then(
                        (m) => m.DashboardComponent
                    ),
            },
            {
                path: 'portfolio',
                loadComponent: () =>
                    import('../shared/trading/portfolio/portfolio.component')
                        .then(m => m.PortfolioComponent),
                data: { seo: { title: 'Trading Portfolio | WEKO Pro', description: 'Manage Weko-Trade accounts, open positions, and trading history.' } }
            },
            {
                path: 'add-trade',
                loadComponent: () =>
                    import('../features/trader/components/add-trade/add-trade.component').then(
                        (m) => m.AddTradeComponent
                    )
            },
            {
                path: 'my-trades',
                loadComponent: () =>
                    import('../features/trader/components/my-trade/my-trade.component').then(
                        (m) => m.MyTradeComponent
                    )
            },
            {
                path: 'trade-clients',
                loadComponent: () =>
                    import('../features/trader/components/trade-clients/trade-clients.component').then(
                        (m) => m.TradeClientsComponent
                    )
            },
            {
                path: 'trade-details/:id',
                loadComponent: () =>
                    import('../features/trader/components/trade-details/trade-details.component')
                        .then(m => m.TradeDetailsComponent)
            }, {
                path: 'profile',
                loadComponent: () =>
                    import('../shared/account/profile/profile.component')
                        .then(m => m.ProfileComponent)
            },
            {
                path: 'change-password',
                loadComponent: () =>
                    import('../shared/account/change-password/change-password.component')
                        .then(m => m.ChangePasswordComponent)
            },
            {
                path: 'settings/change-password',
                loadComponent: () =>
                    import('../shared/account/change-password/change-password.component')
                        .then(m => m.ChangePasswordComponent)
            },
            {
                path: 'leaderboard',
                loadComponent: () =>
                    import('../shared/trading/leaderboard/leaderboard.component')
                        .then(m => m.LeaderboardComponent)
            },
            {
                path: 'results',
                loadComponent: () =>
                    import('../shared/trading/results/results.component')
                        .then(m => m.ResultsComponent)
            },
            {
                path: 'tools',
                children: [
                    {
                        path: 'pip-calculator',
                        loadComponent: () =>
                            import('../shared/trading/calculators/pip-calculator/pip-calculator.component')
                                .then(m => m.PipCalculatorComponent)
                    },
                    {
                        path: 'compound-interest',
                        loadComponent: () =>
                            import('../shared/trading/calculators/compound-interest/compound-interest.component')
                                .then(m => m.CompoundInterestComponent)
                    }

                ]
            },
            {
                path: 'analytics',
                children: [
                    {
                        path: 'chart-analysis',
                        loadComponent: () =>
                            import('../shared/trading/analytics/chart-analysis/chart-analysis.component')
                                .then(m => m.ChartAnalysisComponent)
                    },
                    {
                        path: 'crypto-live',
                        loadComponent: () =>
                            import('../shared/trading/analytics/crypto-live/crypto-live.component')
                                .then(m => m.CryptoLiveComponent)
                    },
                    {
                        path: 'crypto-trends',
                        loadComponent: () =>
                            import('../shared/trading/analytics/crypto-trends/crypto-trends.component')
                                .then(m => m.CryptoTrendsComponent)
                    },
                    {
                        path: 'market-heatmap',
                        loadComponent: () =>
                            import('../shared/trading/analytics/market-heatmap/market-heatmap.component')
                                .then(m => m.MarketHeatmapComponent)
                    },
                    {
                        path: 'seasonality-analysis',
                        loadComponent: () =>
                            import('../shared/trading/analytics/seasonality-analysis/seasonality-analysis.component')
                                .then(m => m.SeasonalityAnalysisComponent)
                    }

                ]
            },
            {
                path: 'ai',
                children: [
                    {
                        path: 'chart-analyzer',
                        loadComponent: () =>
                            import('../shared/trading/ai-suite/chart-analyzer/chart-analyzer.component')
                                .then(m => m.ChartAnalyzerComponent)
                    },
                    {
                        path: 'strategy-generator',
                        loadComponent: () =>
                            import('../shared/trading/ai-suite/strategy-generator/strategy-generator.component')
                                .then(m => m.StrategyGeneratorComponent)
                    },
                    {
                        path: 'rr-assistant',
                        loadComponent: () =>
                            import('../shared/trading/ai-suite/rr-assistant/rr-assistant.component')
                                .then(m => m.RrAssistantComponent)
                    },
                    {
                        path: 'rr-calculation',
                        loadComponent: () =>
                            import('../shared/trading/ai-suite/rr-calculation/rr-calculation.component')
                                .then(m => m.RrCalculationComponent)
                    },
                    {
                        path: 'brain',
                        loadComponent: () =>
                            import('../shared/trading/ai-suite/ai-brain/ai-brain.component')
                                .then(m => m.AiBrainComponent)
                    }
                ]
            }
        ],
    },

    /**
     * =========================
     * FALLBACK ROUTE
     * =========================
     */

    /**
     * =========================
     * FALLBACK ROUTE
     * =========================
     */
    {
        path: '**',
        loadComponent: () =>
            import('../features/public/not-found/not-found.component').then(
                (m) => m.NotFoundComponent
            ),
        data: {
            seo: {
                title: 'Page Not Found | WEKO Pro',
                description: 'The requested page could not be found.',
                robots: 'noindex, nofollow'
            }
        }
    },
];
